import { useState } from "react";
import { Loader2, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useQuoteModal } from "@/context/QuoteModalContext";
import { submitQuoteRequest } from "@/lib/api";
import { PRODUCT_CATEGORIES, BRANDS } from "@/data/site";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  category: "",
  brand: "",
  quantity: "",
  destination: "",
  message: "",
};

export default function QuoteModal() {
  const { open, prefill, setOpenChange } = useQuoteModal();

  return (
    <Dialog open={open} onOpenChange={setOpenChange}>
      <DialogContent
        data-testid="quote-modal"
        className="max-h-[92vh] max-w-2xl overflow-y-auto border-slate-200 bg-white text-slate-800 sm:rounded-xl"
      >
        <DialogHeader className="text-left">
          <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-cyan-600">
            <Sparkles className="h-3.5 w-3.5" />
            One-click quote
          </div>
          <DialogTitle className="font-heading text-2xl font-semibold text-slate-900 sm:text-3xl">
            Request a quotation
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Share a few details and our sales team will respond with pricing
            and lead time within 24 hours.
          </DialogDescription>
        </DialogHeader>

        {open && <QuoteForm prefill={prefill} />}
      </DialogContent>
    </Dialog>
  );
}

function QuoteForm({ prefill }) {
  const { closeModal } = useQuoteModal();
  const [form, setForm] = useState(() => ({ ...emptyForm, ...prefill }));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const handleSelect = (name) => (value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!form.email.trim()) er.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      er.email = "Please enter a valid email.";
    if (!form.category.trim()) er.category = "Please pick a category.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitQuoteRequest(form);
      toast.success(
        "Quote request received — our team will reply within 24 hours."
      );
      closeModal();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Could not submit quote request. Please try again or email us.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="quote-form"
      onSubmit={onSubmit}
      noValidate
      className="mt-2 space-y-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <ModalField
          label="Full name"
          name="name"
          required
          testid="quote-form-name"
          value={form.name}
          onChange={handleChange}
          error={errors.name}
        />
        <ModalField
          label="Email"
          name="email"
          type="email"
          required
          testid="quote-form-email"
          value={form.email}
          onChange={handleChange}
          error={errors.email}
        />
        <ModalField
          label="Phone"
          name="phone"
          testid="quote-form-phone"
          value={form.phone}
          onChange={handleChange}
          optional
        />
        <ModalField
          label="Company"
          name="company"
          testid="quote-form-company"
          value={form.company}
          onChange={handleChange}
          optional
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <ModalLabel htmlFor="category" required>
            Product category
          </ModalLabel>
          <Select
            value={form.category}
            onValueChange={handleSelect("category")}
          >
            <SelectTrigger
              id="category"
              data-testid="quote-form-category"
              className="h-10 border-slate-200 bg-slate-100 text-slate-800"
            >
              <SelectValue placeholder="Choose a category" />
            </SelectTrigger>
            <SelectContent className="border-slate-200 bg-white text-slate-800">
              {PRODUCT_CATEGORIES.map((cat) => (
                <SelectItem
                  key={cat.slug}
                  value={cat.title}
                  data-testid={`quote-category-${cat.slug}`}
                >
                  {cat.title}
                </SelectItem>
              ))}
              <SelectItem value="Other / Custom request">
                Other / Custom request
              </SelectItem>
            </SelectContent>
          </Select>
          {errors.category && (
            <p
              data-testid="quote-form-category-error"
              className="text-xs text-red-400"
            >
              {errors.category}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <ModalLabel htmlFor="brand">Brand preference</ModalLabel>
          <Select
            value={form.brand}
            onValueChange={handleSelect("brand")}
          >
            <SelectTrigger
              id="brand"
              data-testid="quote-form-brand"
              className="h-10 border-slate-200 bg-slate-100 text-slate-800"
            >
              <SelectValue placeholder="Any / no preference" />
            </SelectTrigger>
            <SelectContent className="max-h-72 border-slate-200 bg-white text-slate-800">
              <SelectItem value="Any / no preference">
                Any / no preference
              </SelectItem>
              {BRANDS.map((b) => (
                <SelectItem key={b.name} value={b.name}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <ModalField
          label="Quantity / units"
          name="quantity"
          testid="quote-form-quantity"
          value={form.quantity}
          onChange={handleChange}
          placeholder="e.g. 50 units"
          optional
        />
        <ModalField
          label="Destination"
          name="destination"
          testid="quote-form-destination"
          value={form.destination}
          onChange={handleChange}
          placeholder="e.g. Riyadh, KSA"
          optional
        />
      </div>

      <div className="space-y-2">
        <ModalLabel htmlFor="message">Additional details</ModalLabel>
        <Textarea
          id="message"
          name="message"
          rows={3}
          data-testid="quote-form-message"
          value={form.message}
          onChange={handleChange}
          placeholder="Specific SKUs, configuration, timelines…"
          className="border-slate-200 bg-slate-100 text-slate-800 placeholder:text-slate-500 focus-visible:ring-cyan-400/40"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <p className="text-xs text-slate-500">
          We typically reply within one business day.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          data-testid="quote-form-submit-btn"
          className="rounded-md bg-cyan-600 px-5 py-5 font-medium text-white hover:bg-cyan-700 disabled:opacity-70"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              Request quote
              <Send className="ml-1.5 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

function ModalLabel({ children, htmlFor, required }) {
  return (
    <Label
      htmlFor={htmlFor}
      className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500"
    >
      {children}
      {required && <span className="text-cyan-600"> *</span>}
    </Label>
  );
}

function ModalField({
  label,
  name,
  type = "text",
  value,
  onChange,
  testid,
  placeholder,
  error,
  required = false,
  optional = false,
}) {
  return (
    <div className="space-y-2">
      <ModalLabel htmlFor={name} required={required}>
        {label}
        {optional && <span className="ml-1 text-slate-400">(optional)</span>}
      </ModalLabel>
      <Input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="h-10 border-slate-200 bg-slate-100 text-slate-800 placeholder:text-slate-500 focus-visible:ring-cyan-400/40"
      />
      {error && (
        <p data-testid={`${testid}-error`} className="text-xs text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
